import { AlertTriangle, Check, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  AdminSectionCard,
  ConfirmDialog,
  HelpAnchor,
  InlineError,
  Pill,
  ProviderLogo,
  SkeletonForm,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { BUTTON_GHOST_ADMIN, BUTTON_PRIMARY, INTERIOR_CALLOUT } from '@/shared/ui-class';
import {
  SEED_EMBEDDING_PROVIDER_LOAD,
  type EmbeddingProviderId,
  type EmbeddingProviderLoadResult,
} from '@/versions/v1/shared/admin-seed';
import { AdminSceneShell } from './admin-shared';
import {
  catalogByProvider,
  estimateReembed,
  isEmbeddingConfigDirty,
  pickedTag,
  PROVIDER_LABELS,
  PROVIDER_LOGO_ID,
  PROVIDER_ORDER,
  totalStaleVectors,
} from './EmbeddingProviderScene.transform';

type ReembedWarningModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  totalVectors: number;
  trigger: 'switch' | 'manual';
  onConfirm: () => void | Promise<void>;
};

function ReembedWarningModal({
  open,
  onOpenChange,
  totalVectors,
  trigger,
  onConfirm,
}: ReembedWarningModalProps) {
  const { minutes, costUsd } = useMemo(
    () => estimateReembed(totalVectors),
    [totalVectors],
  );

  const description = (
    <div className="space-y-3 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
      {trigger === 'switch' ? (
        <p>
          Switching the embedding provider re-embeds{' '}
          <strong className="text-zinc-900 dark:text-zinc-100">
            every vector in the system
          </strong>{' '}
          — not just the stale ones. Memory recall and document search will degrade
          until re-embedding completes.
        </p>
      ) : (
        <p>
          This will re-embed{' '}
          <strong className="text-zinc-900 dark:text-zinc-100">
            every vector in the system
          </strong>{' '}
          — memory vectors and project-file vectors — using the active provider. The
          job runs in the background but is CPU- and network-intensive and will compete
          with live traffic.
        </p>
      )}

      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 dark:border-amber-800 dark:bg-amber-950">
        <p className="font-medium text-amber-900 dark:text-amber-200">
          Rough estimate (conservative)
        </p>
        <ul className="mt-1 space-y-0.5 text-xs text-amber-800 dark:text-amber-300">
          <li>
            Total vectors: <strong>{totalVectors.toLocaleString()}</strong>
          </li>
          <li>
            Estimated time:{' '}
            <strong>{minutes < 1 ? '< 1 min' : `~${minutes} min`}</strong>
          </li>
          <li>
            Estimated cost:{' '}
            <strong>
              {costUsd < 0.01 ? '< $0.01' : `~$${costUsd.toFixed(2)}`}
            </strong>{' '}
            USD
          </li>
        </ul>
        <p className="mt-1.5 text-xs text-amber-700 dark:text-amber-400">
          These are rough estimates only. Actual time and cost depend on provider, text
          length, network, and batch concurrency.
        </p>
      </div>

      <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 dark:border-red-900 dark:bg-red-950">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
        <div className="space-y-1 text-xs text-red-800 dark:text-red-300">
          <p className="font-semibold">Maintenance window only</p>
          <p>
            Do <strong>not</strong> run re-embedding on a live, production server.
            Schedule this during a planned maintenance window with reduced or zero user
            load. Embedding provider changes should be rare infrastructure decisions, not
            routine adjustments.
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={
        trigger === 'switch'
          ? 'Confirm embedding provider switch'
          : 'Confirm full re-embed'
      }
      description={description}
      confirmLabel={trigger === 'switch' ? 'Switch & save' : 'Re-embed now'}
      cancelLabel="Cancel"
      onConfirm={onConfirm}
    />
  );
}

function BedrockCredentialBadge({
  status,
}: {
  status: EmbeddingProviderLoadResult['bedrockStatus'];
}) {
  return (
    <div className={cn('mt-2', INTERIOR_CALLOUT)}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
          AWS credential
        </span>
        {status.credentialPresent ? (
          <Pill tone="success">configured</Pill>
        ) : (
          <Pill tone="warning">not set</Pill>
        )}
        <Link
          to="/admin/api-keys"
          className="text-xs text-muted underline hover:text-zinc-700 dark:hover:text-zinc-300"
        >
          {status.credentialPresent ? 'Rotate in API Keys' : 'Set in API Keys'}
        </Link>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-muted">
        <span>
          Region:{' '}
          <span className="font-mono text-zinc-700 dark:text-zinc-300">
            {status.region}
          </span>
        </span>
        <Pill>
          {status.regionSource === 'admin'
            ? 'admin-set'
            : status.regionSource === 'env'
              ? 'from env'
              : 'default'}
        </Pill>
      </div>
      {!status.credentialPresent ? (
        <p className="mt-1.5 text-xs text-muted">
          Set the <span className="font-mono">AWS Bedrock — embeddings</span> key in the{' '}
          <Link
            to="/admin/api-keys"
            className="underline hover:text-zinc-700 dark:hover:text-zinc-300"
          >
            API Keys panel
          </Link>{' '}
          (falls back to the shared Bedrock token when unset) to enable Bedrock embedding
          models.
        </p>
      ) : null}
    </div>
  );
}

function EmbeddingProviderPanelSkeleton() {
  return (
    <div className="space-y-6" aria-hidden>
      <AdminSectionCard title="Active config">
        <SkeletonForm fields={2} />
      </AdminSectionCard>
      <AdminSectionCard title="Provider & model">
        <SkeletonForm fields={4} />
      </AdminSectionCard>
    </div>
  );
}

export function EmbeddingProviderScene() {
  const [loadResult, setLoadResult] = useState(SEED_EMBEDDING_PROVIDER_LOAD);
  const [pickedProvider, setPickedProvider] = useState<EmbeddingProviderId>(
    SEED_EMBEDDING_PROVIDER_LOAD.active.provider,
  );
  const [pickedModel, setPickedModel] = useState(
    SEED_EMBEDDING_PROVIDER_LOAD.active.model,
  );
  const [loading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reembedBusy, setReembedBusy] = useState(false);
  const [error] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [reembedError, setReembedError] = useState<string | null>(null);
  const [warningModal, setWarningModal] = useState<
    { trigger: 'switch' | 'manual' } | null
  >(null);
  const [openProviders, setOpenProviders] = useState<Set<EmbeddingProviderId>>(
    () => new Set<EmbeddingProviderId>(),
  );

  const toggleProvider = useCallback((provider: EmbeddingProviderId) => {
    setOpenProviders((prev) => {
      const next = new Set(prev);
      if (next.has(provider)) next.delete(provider);
      else next.add(provider);
      return next;
    });
  }, []);

  const dirty = isEmbeddingConfigDirty(loadResult, pickedProvider, pickedModel);
  const activeTag = loadResult.active.tag;
  const totalVectors = totalStaleVectors(loadResult);
  const reembedRunning = reembedBusy || loadResult.reembed.running;
  const columnDim = loadResult.columnDim;
  const pickedTagValue = pickedTag(pickedProvider, pickedModel);

  const doSave = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    setLoadResult((prev) => ({
      ...prev,
      active: {
        provider: pickedProvider,
        model: pickedModel,
        tag: pickedTag(pickedProvider, pickedModel),
        source: 'admin',
      },
    }));
    setSaving(false);
  }, [pickedModel, pickedProvider]);

  const onSave = () => {
    if (!dirty) return;
    setWarningModal({ trigger: 'switch' });
  };

  const doReembed = useCallback(async () => {
    setReembedError(null);
    setReembedBusy(true);
    setLoadResult((prev) => ({
      ...prev,
      reembed: {
        running: true,
        startedAt: prev.reembed.startedAt,
        processed: 120,
        embedded: 118,
        failed: 2,
      },
    }));
    setReembedBusy(false);
  }, []);

  const onReembed = () => {
    setWarningModal({ trigger: 'manual' });
  };

  const onWarningConfirm = useCallback(async () => {
    if (!warningModal) return;
    if (warningModal.trigger === 'switch') {
      await doSave();
    } else {
      await doReembed();
    }
  }, [doReembed, doSave, warningModal]);

  const panel = loading ? (
    <EmbeddingProviderPanelSkeleton />
  ) : error ? (
    <InlineError>{error}</InlineError>
  ) : (
    <div className="flex max-w-2xl flex-col gap-6">
      <AdminSectionCard title="Active config">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-mono text-zinc-800 dark:text-zinc-200">
            {loadResult.active.tag}
          </span>
          <Pill>
            {loadResult.active.source === 'admin' ? 'admin-set' : 'env default'}
          </Pill>
          <span className="text-xs text-muted">
            vector store: {columnDim}-dim, fixed
          </span>
        </div>
        <div className="mt-3 flex gap-4 text-xs text-muted">
          <span>
            Stale memory vectors:{' '}
            <strong className="text-zinc-800 dark:text-zinc-200">
              {loadResult.staleMemoryVectors}
            </strong>
          </span>
          <span>
            Stale project-file vectors:{' '}
            <strong className="text-zinc-800 dark:text-zinc-200">
              {loadResult.staleProjectFileVectors}
            </strong>
          </span>
        </div>
      </AdminSectionCard>

      <AdminSectionCard title="Provider & model">
        <div className="flex flex-col gap-3">
          {PROVIDER_ORDER.map((provider) => {
            const entries = catalogByProvider(provider);
            const hasKey = loadResult.keyPresence[provider];
            const open = openProviders.has(provider);
            const hasPickedHere = pickedProvider === provider;
            return (
              <div
                key={provider}
                className="overflow-hidden rounded-lg border border-divider"
              >
                <button
                  type="button"
                  aria-expanded={open}
                  onClick={() => toggleProvider(provider)}
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-900/40"
                >
                  <ProviderLogo
                    provider={PROVIDER_LOGO_ID[provider]}
                    size={18}
                    className="shrink-0"
                  />
                  <span className="text-sm font-medium text-zinc-800 dark:text-zinc-200">
                    {PROVIDER_LABELS[provider]}
                  </span>
                  {provider !== 'bedrock' ? (
                    <Pill className={hasKey ? undefined : 'opacity-60'}>
                      {hasKey ? 'key configured' : 'no API key'}
                    </Pill>
                  ) : null}
                  {!open && hasPickedHere ? (
                    <Pill tone="success">selected</Pill>
                  ) : null}
                  {open ? (
                    <ChevronUp className="ml-auto h-4 w-4 shrink-0 text-muted" />
                  ) : (
                    <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-muted" />
                  )}
                </button>

                {open ? (
                  <div className="border-t border-divider p-3">
                    {provider === 'bedrock' && loadResult.bedrockStatus ? (
                      <BedrockCredentialBadge status={loadResult.bedrockStatus} />
                    ) : null}
                    <div
                      className={cn(
                        'grid gap-2 sm:grid-cols-2',
                        provider === 'bedrock' &&
                          loadResult.bedrockStatus &&
                          'mt-3',
                      )}
                    >
                      {entries.map((entry) => {
                        const id = `${entry.provider}:${entry.model}`;
                        const checked =
                          pickedProvider === entry.provider &&
                          pickedModel === entry.model;
                        const disabled = !entry.selectable;
                        return (
                          <button
                            key={id}
                            type="button"
                            disabled={disabled}
                            aria-pressed={checked}
                            onClick={() => {
                              setPickedProvider(entry.provider);
                              setPickedModel(entry.model);
                            }}
                            className={cn(
                              'flex items-start gap-2.5 rounded-lg border p-3 text-left transition-colors',
                              checked
                                ? 'border-accent-500 bg-zinc-50 dark:bg-zinc-900/60'
                                : 'border-divider hover:bg-zinc-50 dark:hover:bg-zinc-900/40',
                              disabled &&
                                'cursor-not-allowed opacity-50 hover:bg-transparent dark:hover:bg-transparent',
                            )}
                          >
                            <ProviderLogo
                              provider={PROVIDER_LOGO_ID[provider]}
                              size={20}
                              className="mt-0.5 shrink-0"
                            />
                            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                              <span className="text-sm font-medium text-zinc-800 dark:text-zinc-200">
                                {entry.label}
                              </span>
                              {disabled ? (
                                <span className="text-xs text-muted">
                                  requires re-embed + a vector column migration (not
                                  supported in v1) — embeds at {entry.dim}, store is{' '}
                                  {columnDim}
                                </span>
                              ) : (
                                <span className="font-mono text-xs text-muted">
                                  {entry.model} · {entry.dim}-dim
                                </span>
                              )}
                            </span>
                            {checked ? (
                              <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent-500" />
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </AdminSectionCard>

      {dirty ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          <p className="font-medium">Embedding provider switch pending</p>
          <p className="mt-1 text-xs">
            Vectors from different models are not interchangeable. Switching from{' '}
            <span className="font-mono">{activeTag}</span> marks all existing vectors
            stale. Memory recall degrades to recency; project- file search returns
            reduced or empty results until re-embedded. You will be shown a cost and time
            estimate before the change is applied.
          </p>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={onSave}
          className={cn(BUTTON_PRIMARY, 'inline-flex items-center gap-2')}
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving…
            </>
          ) : (
            'Save'
          )}
        </button>

        <button
          type="button"
          disabled={reembedRunning}
          onClick={onReembed}
          className={cn(BUTTON_GHOST_ADMIN, 'inline-flex items-center gap-2')}
        >
          {reembedRunning ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Re-embedding…
            </>
          ) : (
            'Re-embed now'
          )}
        </button>
      </div>

      {reembedRunning ? (
        <p className="text-xs text-muted">
          Progress: {loadResult.reembed.processed} processed,{' '}
          {loadResult.reembed.embedded} embedded, {loadResult.reembed.failed} failed
        </p>
      ) : null}

      {saveError ? <InlineError>{saveError}</InlineError> : null}
      {reembedError ? <InlineError>{reembedError}</InlineError> : null}

      {dirty ? (
        <p className="text-xs text-muted">
          Selected: <span className="font-mono">{pickedTagValue}</span> (unsaved)
        </p>
      ) : null}

      {warningModal ? (
        <ReembedWarningModal
          open={warningModal !== null}
          onOpenChange={(open) => {
            if (!open) setWarningModal(null);
          }}
          totalVectors={totalVectors}
          trigger={warningModal.trigger}
          onConfirm={onWarningConfirm}
        />
      ) : null}
    </div>
  );

  return (
    <AdminSceneShell
      sceneName="EmbeddingProviderScene"
      title="Embeddings"
      description="Default embedding provider for knowledge retrieval."
      headerActions={<HelpAnchor doc="workspace/admin" />}
      stackClassName="space-y-6"
    >
      <Commentable name="Panel">{panel}</Commentable>
    </AdminSceneShell>
  );
}
